import React, { useState, useEffect } from 'react';
import usePersistedTab from '../hooks/usePersistedTab';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Avatar,
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Tab,
  Tabs,
  Badge,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Pagination
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Search,
  FilterList,
  MoreVert,
  Edit,
  Block,
  Check,
  PersonAdd,
  Delete,
  Refresh,
  Send,
  AttachMoney,
  TrendingUp,
  Timeline,
  Group,
  Download,
  Upload,
  Warning,
  CheckCircle,
  Cancel,
  MonetizationOn,
  AccountBalance,
  EmojiEvents,
  Star,
  Diamond,
  Public,
  LocationOn
} from '@mui/icons-material';
import { adminAPI } from '../services/api';
import api from '../services/api';
import { formatNumber, formatDateTime, formatRelativeTime } from '../utils/formatters';
import UserDetailsModal from '../components/UserDetailsModal';
import ReferralDetailsDrawer from '../components/ReferralDetailsDrawer';
import { ruRU } from '../locales/ruRU';
import { CustomGridToolbar } from '../components/CustomGridToolbar';

// Компонент для быстрых действий
const QuickActions = ({ user, onAction, onOpenDetails }) => {
  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      <Tooltip title={user.withdrawal_blacklisted ? "Разблокировать вывод" : "Заблокировать вывод"}>
        <IconButton
          size="small"
          color={user.withdrawal_blacklisted ? "success" : "warning"}
          onClick={() => onAction('toggleWithdrawal', user)}
        >
          {user.withdrawal_blacklisted ? <MonetizationOn fontSize="small" /> : <Cancel fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Tooltip title={user.is_blocked ? "Разблокировать" : "Заблокировать"}>
        <IconButton
          size="small"
          color={user.is_blocked ? "success" : "error"}
          onClick={() => onAction('block', user)}
        >
          {user.is_blocked ? <CheckCircle fontSize="small" /> : <Block fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Удалить пользователя">
        <IconButton
          size="small"
          color="error"
          onClick={() => onAction('delete', user)}
        >
          <Delete fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Открыть карточку">
        <IconButton
          size="small"
          color="primary"
          onClick={() => onOpenDetails(user)}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// Компонент статистики
const UserStats = () => {
  const [stats, setStats] = useState({
    withStakings: 0,
    active: 0,
    new: 0,
    vip: 0,
    total: 0,
    totalBalance: 0
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      // Загружаем пользователей и dashboard статистику параллельно
      const [usersResponse, dashboardResponse] = await Promise.all([
        adminAPI.getUsers({ page: 1, limit: 10000 }),
        api.get('/admin/dashboard')
      ]);

      if (usersResponse.data.success && usersResponse.data.data) {
        const allUsers = usersResponse.data.data.users;

        // Подсчитываем статистику
        const now = new Date();
        const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
        const today = new Date(now.toDateString());

        // Получаем количество пользователей со стейкингами из dashboard
        const usersWithStakings = dashboardResponse.data?.data?.finance?.usersWithStakings || 0;

        setStats({
          withStakings: usersWithStakings,
          active: allUsers.filter(u => new Date(u.last_active) >= today).length,
          new: allUsers.filter(u => new Date(u.created_at) >= weekAgo).length,
          vip: allUsers.filter(u => u.vip_status && u.vip_status !== 'none').length,
          total: usersResponse.data.data.totalCount || allUsers.length,
          totalBalance: allUsers.reduce((sum, u) => sum + (parseFloat(u.balance) || 0), 0)
        });
      }
    } catch (error) {
      console.error('Ошибка загрузки статистики:', error);
    }
  };

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={2}>
        <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
          <CardContent>
            <Typography color="white" variant="body2">Всего</Typography>
            <Typography color="white" variant="h4">{formatNumber(stats.total)}</Typography>
            <Typography color="white" variant="caption">пользователей</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <Card sx={{ background: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)' }}>
          <CardContent>
            <Typography color="white" variant="body2">Активных</Typography>
            <Typography color="white" variant="h4">{formatNumber(stats.active)}</Typography>
            <Typography color="white" variant="caption">за сегодня</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <Card sx={{ background: 'linear-gradient(135deg, #00bcd4 0%, #009688 100%)' }}>
          <CardContent>
            <Typography color="white" variant="body2">Со стейкингами</Typography>
            <Typography color="white" variant="h4">{formatNumber(stats.withStakings)}</Typography>
            <Typography color="white" variant="caption">пользователей</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <Card sx={{ background: 'linear-gradient(135deg, #2196f3 0%, #03a9f4 100%)' }}>
          <CardContent>
            <Typography color="white" variant="body2">Новых</Typography>
            <Typography color="white" variant="h4">{formatNumber(stats.new)}</Typography>
            <Typography color="white" variant="caption">за неделю</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <Card sx={{ background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)' }}>
          <CardContent>
            <Typography color="white" variant="body2">VIP</Typography>
            <Typography color="white" variant="h4">{formatNumber(stats.vip)}</Typography>
            <Typography color="white" variant="caption">пользователей</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <Card sx={{ background: 'linear-gradient(135deg, #9c27b0 0%, #673ab7 100%)' }}>
          <CardContent>
            <Typography color="white" variant="body2">Общий баланс</Typography>
            <Typography color="white" variant="h5">{formatNumber(stats.totalBalance)}</Typography>
            <Typography color="white" variant="caption">SPERM</Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// Компонент для статистики по странам (минималистичный вид)
const CountryStats = () => {
  const [countryData, setCountryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    loadCountryStats();

    // Автообновление каждые 30 секунд
    const interval = setInterval(() => {
      loadCountryStats();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const loadCountryStats = async () => {
    try {
      const response = await api.get('/admin/stats/countries');
      if (response.data.success) {
        setCountryData(response.data.data);
      }
    } catch (error) {
      console.error('Ошибка загрузки статистики по странам:', error);
    } finally {
      setLoading(false);
    }
  };

  // Функция для получения emoji флага из кода страны
  const getCountryFlag = (countryCode) => {
    if (!countryCode) return '🌍';
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  };

  if (loading) {
    return <LinearProgress sx={{ mb: 2 }} />;
  }

  if (countryData.length === 0) {
    return null;
  }

  // Показываем первые 8 стран или все если развернуто
  const displayedCountries = expanded ? countryData : countryData.slice(0, 8);
  const totalUsers = countryData.reduce((sum, c) => sum + parseInt(c.user_count || 0, 10), 0);

  return (
    <Paper sx={{ p: 2, mb: 3, bgcolor: '#1e293b' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#94a3b8' }}>
          <Public fontSize="small" /> Распределение по странам ({totalUsers} всего)
        </Typography>
        {countryData.length > 8 && (
          <Button
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{ color: '#6366f1', textTransform: 'none' }}
          >
            {expanded ? 'Свернуть' : `Ещё ${countryData.length - 8}`}
          </Button>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {displayedCountries.map((country, index) => (
          <Chip
            key={country.country_code || index}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>{getCountryFlag(country.country_code)}</span>
                <span>{country.country || country.country_code || '?'}</span>
                <span style={{ opacity: 0.7 }}>({country.user_count})</span>
                {country.vpn_count > 0 && (
                  <span style={{ color: '#f59e0b', fontSize: '10px' }}>🔐{country.vpn_count}</span>
                )}
              </Box>
            }
            size="small"
            sx={{
              bgcolor: index === 0 ? 'rgba(99, 102, 241, 0.2)' : 'rgba(71, 85, 105, 0.3)',
              color: 'white',
              border: index === 0 ? '1px solid #6366f1' : '1px solid #475569',
              '& .MuiChip-label': { px: 1 }
            }}
          />
        ))}
      </Box>
    </Paper>
  );
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [filterTab, setFilterTab] = usePersistedTab('users', 0);
  const [editDialog, setEditDialog] = useState({ open: false, type: '', user: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editData, setEditData] = useState({});
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, user: null });
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [referralDrawerOpen, setReferralDrawerOpen] = useState(false);
  const [referralDrawerUser, setReferralDrawerUser] = useState(null);
  // Новые состояния для периода и пагинации
  const [period, setPeriod] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const PAGE_SIZE = 50;

  useEffect(() => {
    setCurrentPage(1); // Сброс страницы при смене фильтра
    loadUsers(1);
  }, [filterTab, period]);

  // Поиск с debounce 500ms для серверного поиска
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      loadUsers(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Автообновление данных каждые 30 секунд
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadUsers(currentPage);
    }, 30000); // 30 секунд

    return () => clearInterval(interval);
  }, [autoRefresh, filterTab, period, currentPage]);

  const loadUsers = async (page = currentPage) => {
    try {
      setLoading(true);

      // Фильтры по вкладкам
      const filters = {
        0: {}, // Все
        1: { status: 'active' }, // Активные
        2: { vip: true }, // VIP
        3: { new: true }, // Новые
        4: { banned: true } // Забаненные
      };

      // Удаляем @ в начале если есть (для удобства копирования из Telegram)
      const cleanSearch = searchTerm.replace(/^@/, '').trim();

      const response = await adminAPI.getUsers({
        page: page,
        limit: PAGE_SIZE,
        search: cleanSearch || undefined,
        period: period, // Новый параметр периода
        ...filters[filterTab]
      });

      if (response.data.success && response.data.data) {
        const formattedUsers = response.data.data.users.map(user => ({
          ...user,
          referrals_count: user.referral_count || 0
        }));
        setUsers(formattedUsers);
        setTotalPages(response.data.data.totalPages || 1);
        setTotalUsers(response.data.data.totalCount || response.data.data.total || 0);
      } else {
        setUsers([]);
        setTotalPages(1);
        setTotalUsers(0);
      }
    } catch (error) {
      console.error('Ошибка загрузки пользователей:', error);
      setUsers([]);
      setTotalPages(1);
      setTotalUsers(0);
    } finally {
      setLoading(false);
    }
  };

  // Обработчик смены страницы
  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
    loadUsers(newPage);
  };

  // Обработчик смены периода
  const handlePeriodChange = (event, newPeriod) => {
    if (newPeriod !== null) {
      setPeriod(newPeriod);
    }
  };

  const handleOpenUserDetails = (user) => {
    setSelectedUser(user);
    setDetailsOpen(true);
  };

  const handleOpenReferralDetails = (user) => {
    setReferralDrawerUser(user);
    setReferralDrawerOpen(true);
  };

  const handleQuickAction = async (action, user) => {
    switch(action) {
      case 'notify':
        setEditDialog({
          open: true,
          type: 'notify',
          user
        });
        setEditData({
          message: '',
          type: 'info'
        });
        break;

      case 'warn':
        setEditDialog({
          open: true,
          type: 'warn',
          user
        });
        setEditData({
          reason: '',
          comment: ''
        });
        break;

      case 'block':
        if (user.is_blocked) {
          // Разблокировать сразу
          await handleUnblockUser(user.id);
        } else {
          // Открыть диалог блокировки с опциями
          setEditDialog({
            open: true,
            type: 'block',
            user
          });
          setEditData({
            reason: '',
            blockType: 'permanent',
            until: ''
          });
        }
        break;

      case 'toggleWithdrawal':
        setEditDialog({
          open: true,
          type: 'toggleWithdrawal',
          user
        });
        setEditData({
          reason: ''
        });
        break;

      case 'delete':
        setDeleteConfirmDialog({ open: true, user });
        break;
    }
  };

  const handleBlockUser = async (userId, data) => {
    try {
      await adminAPI.blockUser(userId, data);
      setSnackbar({
        open: true,
        message: 'Пользователь заблокирован',
        severity: 'success'
      });
      await loadUsers();
    } catch (error) {
      console.error('Ошибка блокировки пользователя:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Ошибка при блокировке пользователя',
        severity: 'error'
      });
    }
  };

  const handleUnblockUser = async (userId) => {
    try {
      await adminAPI.unblockUser(userId);
      setSnackbar({
        open: true,
        message: 'Пользователь разблокирован',
        severity: 'success'
      });
      await loadUsers();
    } catch (error) {
      console.error('Ошибка разблокировки пользователя:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка при разблокировке пользователя',
        severity: 'error'
      });
    }
  };

  const handleWarnUser = async (userId, data) => {
    try {
      const response = await adminAPI.warnUser(userId, data);
      const autoBlocked = response.data?.data?.auto_blocked;

      setSnackbar({
        open: true,
        message: autoBlocked
          ? 'Предупреждение выдано. Пользователь автоматически заблокирован (3 предупреждения)'
          : 'Предупреждение выдано',
        severity: autoBlocked ? 'warning' : 'success'
      });
      await loadUsers();
    } catch (error) {
      console.error('Ошибка выдачи предупреждения:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Ошибка при выдаче предупреждения',
        severity: 'error'
      });
    }
  };

  const handleEditSubmit = async () => {
    try {
      const { type, user } = editDialog;

      switch(type) {
        case 'block':
          const blockData = {
            reason: editData.reason
          };

          if (editData.blockType === 'temporary') {
            blockData.until = editData.until;
          }

          await handleBlockUser(user.id, blockData);
          break;

        case 'toggleWithdrawal':
          await api.post(`/admin/users/${user.id}/toggle-withdrawal`, {
            reason: editData.reason
          });

          setSnackbar({
            open: true,
            message: user.withdrawal_blacklisted
              ? `Вывод разблокирован для ${user.username || user.telegram_id}`
              : `Вывод заблокирован для ${user.username || user.telegram_id}`,
            severity: 'success'
          });
          break;
      }

      setEditDialog({ open: false, type: '', user: null });
      setEditData({});
      await loadUsers();
    } catch (error) {
      console.error('Ошибка при выполнении действия:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка при выполнении действия',
        severity: 'error'
      });
    }
  };

  const handleDeleteUser = async () => {
    try {
      const { user } = deleteConfirmDialog;
      await adminAPI.deleteUser(user.id);
      
      setSnackbar({
        open: true,
        message: `Пользователь ${user.username || user.telegram_id} успешно удален`,
        severity: 'success'
      });
      
      setDeleteConfirmDialog({ open: false, user: null });
      await loadUsers();
    } catch (error) {
      console.error('Ошибка удаления пользователя:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка при удалении пользователя',
        severity: 'error'
      });
    }
  };

  const handleExport = async () => {
    try {
      const response = await adminAPI.get('/users/export', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `users_${new Date().toISOString()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setSnackbar({
        open: true,
        message: 'Данные экспортированы',
        severity: 'success'
      });
    } catch (error) {
      console.error('Ошибка экспорта:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка при экспорте данных',
        severity: 'error'
      });
    }
  };

  const columns = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 70 
    },
    {
      field: 'username',
      headerName: 'Пользователь',
      width: 200,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            '&:hover': { opacity: 0.7 }
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenUserDetails(params.row);
          }}
        >
          <Avatar sx={{ width: 32, height: 32 }}>
            {params.row.username?.[0]?.toUpperCase() || '?'}
          </Avatar>
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: 'primary.main',
                textDecoration: 'underline',
                fontWeight: 500
              }}
            >
              {params.row.username || `User ${params.row.telegram_id}`}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              ID: {params.row.telegram_id}
            </Typography>
          </Box>
        </Box>
      )
    },
    {
      field: 'balance',
      headerName: 'Баланс',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={formatNumber(params.value || 0)}
          size="small"
          sx={{ bgcolor: 'primary.main', color: 'white' }}
        />
      )
    },
    {
      field: 'rank',
      headerName: 'Ранг',
      width: 120,
      renderCell: (params) => {
        const rankColors = {
          'newbie': '#757575',
          'bronze': '#CD7F32',
          'silver': '#C0C0C0',
          'gold': '#FFD700',
          'platinum': '#E5E4E2'
        };
        return (
          <Chip
            label={params.value || 'Новичок'}
            size="small"
            sx={{ 
              bgcolor: rankColors[params.value] || '#757575',
              color: 'white'
            }}
          />
        );
      }
    },
    {
      field: 'vip_level',
      headerName: 'VIP',
      width: 100,
      renderCell: (params) => {
        if (!params.value) return '-';
        const vipColors = ['', '#CD7F32', '#C0C0C0', '#FFD700', '#E5E4E2'];
        return (
          <Chip
            icon={<Star />}
            label={`VIP ${params.value}`}
            size="small"
            sx={{ 
              bgcolor: vipColors[params.value] || '#757575',
              color: 'white'
            }}
          />
        );
      }
    },
    {
      field: 'referrals_count',
      headerName: 'Рефералы',
      width: 100,
      renderCell: (params) => (
        <Box
          sx={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            '&:hover': {
              opacity: 0.7
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenReferralDetails(params.row);
          }}
        >
          <Badge badgeContent={params.value} color="primary">
            <Group />
          </Badge>
        </Box>
      )
    },
    {
      field: 'last_active',
      headerName: 'Активность',
      width: 120,
      renderCell: (params) => (
        <Typography variant="caption">
          {params.value ? formatRelativeTime(params.value) : 'Никогда'}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Статус',
      width: 140,
      renderCell: (params) => {
        const statusConfig = {
          'banned': { label: 'Забанен', color: 'error', icon: <Cancel /> },
          'leader': { label: 'Лидер', color: 'warning', icon: <EmojiEvents /> },
          'working': { label: 'Работает', color: 'info', icon: <Group /> },
          'active': { label: 'Активен', color: 'success', icon: <CheckCircle /> },
          'inactive': { label: 'Не активен', color: 'default', icon: <Cancel /> }
        };

        const config = statusConfig[params.value] || statusConfig['inactive'];

        return (
          <Chip
            icon={config.icon}
            label={config.label}
            size="small"
            color={config.color}
          />
        );
      }
    },
    {
      field: 'geo',
      headerName: 'GEO',
      width: 220,
      sortable: false,
      renderCell: (params) => {
        const { ip_address, country_code, country, city, is_vpn_user } = params.row;

        // Функция для получения emoji флага из кода страны
        const getCountryFlag = (countryCode) => {
          if (!countryCode) return '🌍';
          const codePoints = countryCode
            .toUpperCase()
            .split('')
            .map(char => 127397 + char.charCodeAt());
          return String.fromCodePoint(...codePoints);
        };

        if (!ip_address) {
          return (
            <Typography variant="caption" color="textSecondary">
              Нет данных
            </Typography>
          );
        }

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <LocationOn fontSize="small" sx={{ color: 'text.secondary', fontSize: 16 }} />
              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                {ip_address}
              </Typography>
              {is_vpn_user && (
                <Chip
                  label="🔐 VPN"
                  size="small"
                  sx={{
                    bgcolor: '#f59e0b',
                    color: 'white',
                    fontSize: '10px',
                    height: '18px',
                    fontWeight: 600
                  }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2">
                {getCountryFlag(country_code)} {country || country_code || '?'}
                {city && city !== 'Неизвестно' && `, ${city}`}
              </Typography>
            </Box>
          </Box>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Действия',
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <QuickActions
          user={params.row}
          onAction={handleQuickAction}
          onOpenDetails={handleOpenUserDetails}
        />
      )
    }
  ];

  // Фильтрация теперь на сервере - используем данные напрямую
  const filteredUsers = users;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      {/* Заголовок */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <Typography variant="h4">
          Управление пользователями
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Chip
            icon={<Refresh />}
            label={autoRefresh ? 'Авто-обновление: ВКЛ' : 'Авто-обновление: ВЫКЛ'}
            color={autoRefresh ? 'success' : 'default'}
            onClick={() => setAutoRefresh(!autoRefresh)}
            size="small"
            sx={{ cursor: 'pointer' }}
          />
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExport}
          >
            Экспорт
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadUsers}
          >
            Обновить
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => {/* Добавить пользователя */}}
          >
            Добавить
          </Button>
        </Box>
      </Box>

      {/* Статистика */}
      <UserStats />

      {/* Статистика по странам */}
      <CountryStats />

      {/* Вкладки фильтров */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={filterTab}
          onChange={(e, v) => setFilterTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Все пользователи" />
          <Tab label="Активные" />
          <Tab label="VIP" />
          <Tab label="Новые" />
          <Tab label="Забаненные" />
        </Tabs>
      </Paper>

      {/* Переключатель периодов */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="textSecondary">Период активности:</Typography>
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={handlePeriodChange}
            size="small"
          >
            <ToggleButton value="today" sx={{ textTransform: 'none' }}>
              Сегодня
            </ToggleButton>
            <ToggleButton value="7days" sx={{ textTransform: 'none' }}>
              7 дней
            </ToggleButton>
            <ToggleButton value="month" sx={{ textTransform: 'none' }}>
              Месяц
            </ToggleButton>
            <ToggleButton value="3months" sx={{ textTransform: 'none' }}>
              3 месяца
            </ToggleButton>
            <ToggleButton value="all" sx={{ textTransform: 'none' }}>
              Всё время
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Typography variant="body2" color="textSecondary">
          Найдено: <strong>{totalUsers}</strong> пользователей
        </Typography>
      </Paper>

      {/* Поиск */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Поиск по @username или Telegram ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            )
          }}
          helperText="Можно вводить @username напрямую из Telegram"
        />
      </Paper>

      {/* Таблица */}
      <Paper sx={{ height: 'auto', minHeight: 600 }}>
        {loading && <LinearProgress />}
        <DataGrid
          rows={filteredUsers}
          columns={columns}
          autoHeight
          hideFooterPagination
          hideFooter
          checkboxSelection={selectionMode}
          disableSelectionOnClick
          loading={loading}
          getRowId={(row) => row.id}
          components={{
            Toolbar: () => (
              <CustomGridToolbar
                rows={filteredUsers}
                columns={columns}
                filename={`users_${new Date().toISOString().split('T')[0]}.xlsx`}
              />
            )
          }}
          localeText={ruRU}
          onSelectionModelChange={(ids) => setSelectedRows(ids)}
          sx={{
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(224, 224, 224, 0.5)'
            }
          }}
        />
      </Paper>

      {/* Пагинация */}
      {totalPages > 1 && (
        <Paper sx={{ p: 2, mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            Страница {currentPage} из {totalPages} (показано {users.length} из {totalUsers})
          </Typography>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            siblingCount={2}
          />
        </Paper>
      )}

      {/* Диалог блокировки вывода */}
      <Dialog
        open={editDialog.open && editDialog.type === 'toggleWithdrawal'}
        onClose={() => setEditDialog({ open: false, type: '', user: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {editDialog.user?.withdrawal_blacklisted ? <MonetizationOn color="success" /> : <Cancel color="warning" />}
          {editDialog.user?.withdrawal_blacklisted ? 'Разблокировать вывод' : 'Заблокировать вывод'}
        </DialogTitle>
        <DialogContent>
          <Alert severity={editDialog.user?.withdrawal_blacklisted ? "info" : "warning"} sx={{ mb: 2 }}>
            {editDialog.user?.withdrawal_blacklisted
              ? 'Пользователь снова сможет выводить средства'
              : 'Пользователь не сможет выводить средства (кнопка вывода станет неактивной)'
            }
          </Alert>
          {editDialog.user && (
            <Box sx={{ mb: 2, p: 2, bgcolor: '#334155', borderRadius: 1, color: '#e2e8f0' }}>
              <Typography variant="body2"><strong>Пользователь:</strong> {editDialog.user.username || editDialog.user.telegram_id}</Typography>
              <Typography variant="body2"><strong>Баланс:</strong> {formatNumber(editDialog.user.balance || 0)} SPERM</Typography>
            </Box>
          )}
          {!editDialog.user?.withdrawal_blacklisted && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Причина блокировки (опционально)"
                  multiline
                  rows={2}
                  value={editData.reason || ''}
                  onChange={(e) => setEditData({ ...editData, reason: e.target.value })}
                  placeholder="Можно оставить пустым"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, type: '', user: null })}>
            Отмена
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            color={editDialog.user?.withdrawal_blacklisted ? "success" : "warning"}
            startIcon={editDialog.user?.withdrawal_blacklisted ? <MonetizationOn /> : <Cancel />}
          >
            {editDialog.user?.withdrawal_blacklisted ? 'Разблокировать' : 'Заблокировать'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог блокировки пользователя */}
      <Dialog
        open={editDialog.open && editDialog.type === 'block'}
        onClose={() => setEditDialog({ open: false, type: '', user: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Block color="error" />
          Заблокировать пользователя
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Пользователь не сможет войти в приложение до снятия блокировки
          </Alert>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Причина блокировки (опционально)"
                multiline
                rows={3}
                value={editData.reason || ''}
                onChange={(e) => setEditData({ ...editData, reason: e.target.value })}
                placeholder="Можно оставить пустым"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Тип блокировки</InputLabel>
                <Select
                  value={editData.blockType || 'permanent'}
                  onChange={(e) => setEditData({ ...editData, blockType: e.target.value })}
                >
                  <MenuItem value="permanent">Постоянная</MenuItem>
                  <MenuItem value="temporary">Временная</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {editData.blockType === 'temporary' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  type="datetime-local"
                  label="Заблокирован до"
                  value={editData.until || ''}
                  onChange={(e) => setEditData({ ...editData, until: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, type: '', user: null })}>
            Отмена
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            color="error"
            startIcon={<Block />}
          >
            Заблокировать
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог подтверждения удаления */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, user: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="error" />
          Подтверждение удаления
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Внимание! Это действие необратимо.
          </Alert>
          {deleteConfirmDialog.user && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Вы действительно хотите удалить пользователя?
              </Typography>
              <Box sx={{ mt: 2, p: 2, bgcolor: '#334155', borderRadius: 1, color: '#e2e8f0' }}>
                <Typography variant="body2"><strong>ID:</strong> {deleteConfirmDialog.user.id}</Typography>
                <Typography variant="body2"><strong>Имя:</strong> {deleteConfirmDialog.user.username || 'Не указано'}</Typography>
                <Typography variant="body2"><strong>Telegram ID:</strong> {deleteConfirmDialog.user.telegram_id}</Typography>
                <Typography variant="body2"><strong>Баланс:</strong> {formatNumber(deleteConfirmDialog.user.balance || 0)} SPERM</Typography>
              </Box>
              <Alert severity="info" sx={{ mt: 2 }}>
                Будут удалены:
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li>Все транзакции пользователя</li>
                  <li>Все стейкинги и депозиты</li>
                  <li>История игр и достижения</li>
                  <li>Реферальные связи</li>
                  <li>Все остальные данные пользователя</li>
                </ul>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmDialog({ open: false, user: null })}>
            Отмена
          </Button>
          <Button 
            onClick={handleDeleteUser} 
            variant="contained" 
            color="error"
            startIcon={<Delete />}
          >
            Удалить безвозвратно
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar для уведомлений */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Модальное окно с деталями пользователя */}
      {selectedUser && (
        <UserDetailsModal
          open={detailsOpen}
          onClose={() => {
            setDetailsOpen(false);
            setSelectedUser(null);
          }}
          userId={selectedUser.id}
          onUserUpdated={loadUsers}
        />
      )}

      {/* Drawer с реферальной структурой */}
      {referralDrawerUser && (
        <ReferralDetailsDrawer
          open={referralDrawerOpen}
          onClose={() => {
            setReferralDrawerOpen(false);
            setReferralDrawerUser(null);
          }}
          userId={referralDrawerUser.id}
          username={referralDrawerUser.username || referralDrawerUser.telegram_id}
        />
      )}
    </Box>
  );
};

export default Users;